/**
 * Stateful loader for a seller's booking resources (calendars). Shared by the
 * top-level BookingManager and any subcomponent that needs the resource list;
 * mutators keep the in-memory list in sync so panes don't each refetch.
 */
import { useCallback, useEffect, useState } from 'react';
import { useBookingApi } from './bookingApi';

export function useBookingResources({ autoLoad = true } = {}) {
    const api = useBookingApi();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(autoLoad);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await api.listResources();
            setResources(list);
            return list;
        } catch (err) {
            setError(err.message || 'Failed to load calendars.');
            return [];
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (autoLoad) reload();
    }, [autoLoad, reload]);

    const upsertLocal = useCallback((resource) => {
        setResources(prev => {
            const idx = prev.findIndex(r => r.id === resource.id);
            if (idx === -1) return [...prev, resource];
            const next = prev.slice();
            next[idx] = resource;
            return next;
        });
    }, []);

    const removeLocal = useCallback((id) => {
        setResources(prev => prev.filter(r => r.id !== id));
    }, []);

    const create = useCallback(async (payload) => {
        const resource = await api.createResource(payload);
        upsertLocal(resource);
        return resource;
    }, [api, upsertLocal]);

    const update = useCallback(async (id, payload) => {
        const resource = await api.updateResource(id, payload);
        upsertLocal(resource);
        return resource;
    }, [api, upsertLocal]);

    const remove = useCallback(async (id) => {
        const result = await api.deleteResource(id);
        // The API deactivates (keeps) resources that still have bookings.
        if (result && result.meta && result.meta.deactivated && result.data) {
            upsertLocal(result.data);
        } else {
            removeLocal(id);
        }
        return result;
    }, [api, removeLocal, upsertLocal]);

    const ensureDefault = useCallback(async () => {
        const resource = await api.ensureDefaultResource();
        upsertLocal(resource);
        return resource;
    }, [api, upsertLocal]);

    return {
        resources,
        loading,
        error,
        reload,
        create,
        update,
        remove,
        ensureDefault,
        upsertLocal,
    };
}

export default useBookingResources;
